import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import { ButtonBase, Stack, Typography } from '@mui/material';
import { FunnelStatuses } from 'features/co-pilot/co-pilot.types';
import PaiHelpIcon from 'features/ui/base-components/pai-help-icon';
import PaiTooltip from 'features/ui/base-components/pai-tooltip';
import PaiLink from 'features/ui/base-components/pai-link';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { FunnelOverview } from './campaign-overview.types';

interface Props {
  funnel: FunnelOverview;
  status: FunnelStatuses;
  startFunnelLoading: boolean;
  inline?: boolean;

  handleEditFunnel?(funnelId: string): () => void;

  handleRestartFunnel(funnelId: string): () => void;

  handleAccountManagementRedirect(event: React.MouseEvent): void;

  handleOpenEditCampaignLinkedinAccountDialog(funnel: FunnelOverview): () => void;

  /** Öffnet den Freigabe-Dialog; ohne Handler bleibt der Freigabe-Hinweis aus. */
  handleOpenApproveMessages?(): void;

  /** Öffnet die Kontaktfreigabe; ohne Handler bleibt der Hinweis aus. */
  handleOpenApproveContacts?(): void;
}

const draft = [FunnelStatuses.CREATED];
const agentWorking = [FunnelStatuses.STARTED];
const agentTakesBreak = [FunnelStatuses.PAUSED];
const agentTurnedOff = [FunnelStatuses.SUSPENDED];
const agentNeedsHelp = [
  FunnelStatuses.ACCOUNT_NOT_FOUND,
  FunnelStatuses.LINKEDIN_ACCOUNT_PROBLEM,
  FunnelStatuses.CO_PILOT_LICENSE_EXPIRED,
  FunnelStatuses.CONTACTS_ALMOST_EXHAUSTED,
];
const agentDone = [FunnelStatuses.FINISHED];
const archived = [FunnelStatuses.ARCHIVED];

/** Campaign was paused automatically because its LinkedIn account needs manual re-authentication. */
const isPausedByAccountLock = (status: FunnelStatuses, funnel: FunnelOverview) =>
  status === FunnelStatuses.PAUSED && funnel.pausedByAccountLock === true;

const CampaignStatus: React.FC<Props> = ({
  funnel,
  status,
  startFunnelLoading,
  inline = false,
  handleEditFunnel,
  handleRestartFunnel,
  handleAccountManagementRedirect,
  handleOpenEditCampaignLinkedinAccountDialog,
  handleOpenApproveMessages,
  handleOpenApproveContacts,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const getStatusTooltip = () => {
    if (isPausedByAccountLock(status, funnel)) {
      return t('campaignStatusPausedByAccountLockDescription');
    }
    switch (status) {
      case FunnelStatuses.STARTED:
        return t('campaignStatusStartedDescription');
      case FunnelStatuses.PAUSED:
        return t('campaignStatusActuallyPausedDescription');
      case FunnelStatuses.SUSPENDED:
        return t('campaignStatusSuspendedDescription');
      case FunnelStatuses.CREATED:
        return t('campaignStatusCreatedDescription');
      case FunnelStatuses.ACCOUNT_NOT_FOUND:
        return t('campaignStatusAccountNotFoundDescription');
      case FunnelStatuses.LINKEDIN_ACCOUNT_PROBLEM:
        return t('campaignStatusLinkedinAccountProblemDescription');
      case FunnelStatuses.CO_PILOT_LICENSE_EXPIRED:
        return t('campaignStatusCoPilotLicenseExpiredDescription');
      case FunnelStatuses.CONTACTS_ALMOST_EXHAUSTED:
        return t('campaignStatusContactsAlmostExhaustedDescription');
      case FunnelStatuses.FINISHED:
        return t('campaignStatusFinishedDescription');
      case FunnelStatuses.ARCHIVED:
        return t('campaignStatusArchivedDescription');
      default:
        return false;
    }
  };

  const pendingApprovals = funnel.toBeApprovedMessages ?? 0;
  const pendingContactApprovals = funnel.toBeApprovedContacts ?? 0;

  const getStatusActionText = () => {
    // Wartende Kontakte zuerst: ohne ihre Freigabe beginnt keine Ansprache, also entsteht auch keine Nachricht.
    if (pendingContactApprovals > 0 && handleOpenApproveContacts) {
      return (
        <PaiTooltip title={t('contactsWaitingForApprovalHint')} variant={'default'}>
          <ButtonBase className={classes.approvalButton} onClick={handleOpenApproveContacts}>
            <HowToRegOutlinedIcon />
            <Typography className={classes.approvalText}>{t('contactsWaitingForApproval', { count: pendingContactApprovals })}</Typography>
          </ButtonBase>
        </PaiTooltip>
      );
    }
    // Wartende Freigaben sind die dringendste Handlung — sie gehen vor „Du musst nichts tun".
    if (pendingApprovals > 0 && handleOpenApproveMessages) {
      return (
        <PaiTooltip title={t('messagesWaitingForApprovalHint')} variant={'default'}>
          <ButtonBase className={classes.approvalButton} onClick={handleOpenApproveMessages}>
            <MarkEmailUnreadOutlinedIcon />
            <Typography className={classes.approvalText}>{t('messagesWaitingForApproval', { count: pendingApprovals })}</Typography>
          </ButtonBase>
        </PaiTooltip>
      );
    }
    if (isPausedByAccountLock(status, funnel)) {
      return (
        <PaiLink
          text={t('linkedinAccountProblem')}
          size={'md'}
          variant={'primary'}
          underline={'always'}
          action={handleAccountManagementRedirect}
        />
      );
    }
    switch (status) {
      case FunnelStatuses.STARTED:
      case FunnelStatuses.PAUSED:
        return <Typography className={classes.text}>{t('doNothing')}</Typography>;
      case FunnelStatuses.SUSPENDED:
        return (
          <PaiLink
            text={t('agentRestart')}
            size={'md'}
            variant={'default'}
            underline={'hover'}
            disabled={startFunnelLoading}
            action={handleRestartFunnel(funnel._id)}
          />
        );
      case FunnelStatuses.CREATED:
        return <PaiLink text={t('completeCampaign')} size={'md'} variant={'primary'} underline={'always'} action={handleEditFunnel?.(funnel._id)} />;
      case FunnelStatuses.ACCOUNT_NOT_FOUND:
        return (
          <PaiLink
            text={t('accountNotFound')}
            size={'md'}
            variant={'primary'}
            underline={'always'}
            disabled={startFunnelLoading}
            action={handleOpenEditCampaignLinkedinAccountDialog(funnel)}
          />
        );
      case FunnelStatuses.LINKEDIN_ACCOUNT_PROBLEM:
        return (
          <PaiLink text={t('linkedinAccountProblem')} size={'md'} variant={'primary'} underline={'always'} action={handleAccountManagementRedirect} />
        );
      case FunnelStatuses.CO_PILOT_LICENSE_EXPIRED:
        return (
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <Typography className={classes.text}>{t('coPilotLicenseExpired')}</Typography>
            <PaiHelpIcon tooltipText={t('coPilotLicenseExpiredDescription')} variant={'warning'} size={'md'} />
          </Stack>
        );
      case FunnelStatuses.CONTACTS_ALMOST_EXHAUSTED:
        return (
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <Typography className={classes.text}>{t('contactsAlmostExhausted')}</Typography>
            <PaiHelpIcon tooltipText={t('contactsAlmostExhaustedDescription')} size={'md'} />
          </Stack>
        );
      case FunnelStatuses.FINISHED:
      case FunnelStatuses.ARCHIVED:
      default:
        return <></>;
    }
  };

  return (
    <Stack gap={inline ? 1 : '6px'} direction={inline ? 'row' : 'column'} alignItems={inline ? 'center' : 'flex-start'}>
      <PaiTooltip title={getStatusTooltip()} variant={'default'}>
        <div
          className={cx(
            classes.status,
            isPausedByAccountLock(status, funnel)
              ? 'accountLock'
              : agentWorking.includes(status)
              ? 'working'
              : agentTakesBreak.includes(status)
              ? 'break'
              : agentNeedsHelp.includes(status)
              ? 'needsHelp'
              : agentDone.includes(status)
              ? 'done'
              : archived.includes(status)
              ? 'archived'
              : draft.includes(status)
              ? 'draft'
              : agentTurnedOff.includes(status)
              ? 'turnedOff'
              : undefined
          )}
        >
          <Typography className={classes.statusText}>
            {isPausedByAccountLock(status, funnel)
              ? t('campaignPausedByAccountLock')
              : agentWorking.includes(status)
              ? t('campaignIsWorking')
              : agentTakesBreak.includes(status)
              ? t('campaignTakesBreak')
              : agentNeedsHelp.includes(status)
              ? t('campaignNeedsHelp')
              : agentDone.includes(status)
              ? t('campaignDone')
              : archived.includes(status)
              ? t('campaignArchived')
              : draft.includes(status)
              ? t('campaignDraft')
              : agentTurnedOff.includes(status)
              ? t('campaignTurnedOff')
              : status}
          </Typography>
        </div>
      </PaiTooltip>

      <div>{getStatusActionText()}</div>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  status: {
    borderRadius: '10px',
    width: 'fit-content',
    padding: '2px 10px',
    '&.working': {
      background: theme.palette.backgrounds.lightGreen,
    },
    '&.break': {
      background: theme.palette.backgrounds.lightOrange,
    },
    '&.needsHelp': {
      background: theme.palette.backgrounds.lightRed,
    },
    '&.done': {
      background: theme.palette.backgrounds.lightViolet,
    },
    '&.archived': {
      background: theme.palette.greys.light,
    },
    '&.draft': {
      background: theme.palette.greys.lighter,
    },
    '&.turnedOff': {
      background: theme.palette.backgrounds.lightOrange,
    },
    '&.accountLock': {
      background: theme.palette.backgrounds.lightRed,
      border: `1px solid ${theme.palette.error.light}`,
    },
  },
  statusTextContainer: {
    padding: '0 8px',
  },
  statusText: {
    fontSize: theme.typography.pxToRem(14),
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  approvalButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '3px 10px',
    borderRadius: '8px',
    background: theme.palette.backgrounds.lightOrange,
    border: `1px solid ${theme.palette.warning.main}`,
    color: theme.palette.warning.dark,
    '& svg': {
      fontSize: theme.typography.pxToRem(16),
    },
    '&:hover': {
      filter: 'brightness(0.97)',
    },
  },
  approvalText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
    color: 'inherit',
  },
}));

export default CampaignStatus;
